import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { ShoppingBag, Menu, X, Check } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useCart } from "@/context/CartContext";
import { LANGS } from "@/i18n";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";

export const Logo = ({ className = "" }) => (
  <a href="#top" data-testid="logo" className={`flex items-center ${className}`}>
    <img src="/logo_aponiqo_dark.png" alt="aponiqo" className="h-8 w-auto lg:h-9" />
  </a>
);

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

const Header = () => {
  const { lang, setLang, t } = useLang();
  const { count, setOpen } = useCart();
  const [scrolled, setScrolled] = useState(false);
  const [mobile, setMobile] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { id: "about", label: t.nav.about },
    { id: "how", label: t.nav.how },
    { id: "products", label: t.nav.products },
    { id: "contact", label: t.nav.contact },
  ];

  return (
    <motion.header
      initial={{ y: -80 }} animate={{ y: 0 }} transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${scrolled ? "glass shadow-[0_2px_20px_rgba(10,37,64,0.06)]" : "bg-transparent"}`}
      data-testid="site-header"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-10">
        <Logo />
        <nav className="hidden items-center gap-8 md:flex">
          {links.map((l) => (
            <button key={l.id} onClick={() => scrollTo(l.id)} data-testid={`nav-${l.id}`}
              className="font-medium text-sm text-foreground/70 transition-colors hover:text-accent">
              {l.label}
            </button>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger data-testid="lang-switcher"
              className="rounded-full border border-border px-3 py-1.5 text-sm font-semibold text-primary transition-colors hover:border-accent">
              {LANGS.find((l) => l.code === lang)?.label}
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[120px]">
              {LANGS.map((l) => (
                <DropdownMenuItem key={l.code} data-testid={`lang-${l.code}`} onClick={() => setLang(l.code)}
                  className="flex items-center justify-between">
                  {l.label}{lang === l.code && <Check className="h-4 w-4 text-accent" />}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <button onClick={() => setOpen(true)} data-testid="cart-button"
            className="relative rounded-full border border-border p-2.5 text-primary transition-colors hover:border-accent">
            <ShoppingBag className="h-5 w-5" />
            {count > 0 && (
              <span data-testid="cart-count" className="absolute -right-1 -top-1 grid h-5 w-5 place-items-center rounded-full bg-accent text-[11px] font-bold text-white">
                {count}
              </span>
            )}
          </button>

          <button onClick={() => scrollTo("products")} data-testid="header-order-btn"
            className="hidden rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition-transform hover:bg-secondary active:scale-95 sm:block">
            {t.nav.order}
          </button>

          <button className="p-2 md:hidden" onClick={() => setMobile(!mobile)} data-testid="mobile-menu-btn">
            {mobile ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {mobile && (
        <div className="glass border-t border-border px-6 py-4 md:hidden">
          {links.map((l) => (
            <button key={l.id} onClick={() => { scrollTo(l.id); setMobile(false); }}
              className="block w-full py-3 text-left font-medium text-primary">
              {l.label}
            </button>
          ))}
        </div>
      )}
    </motion.header>
  );
};

export default Header;
